import { useState } from "react";
import type { SnmpTarget } from "../api/types";
import { useSnmpTargets } from "../hooks/queries";
import { DeviceStatusBadge } from "../components/domain/Badges";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { Pagination } from "../components/ui/Pagination";
import { TableSkeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { Badge } from "../components/ui/Badge";
import { formatDateTime, formatInteger, formatRelative } from "../lib/format";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}

export function SnmpTargetsPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [selected, setSelected] = useState<SnmpTarget | null>(null);
  const query = useSnmpTargets(page, perPage);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">SNMP Hedefleri</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          SNMP ile izlenen ağ cihazları (switch, router, AP, firewall).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>İzlenen ağ cihazları</CardTitle>
          <CardDescription>
            Port trafik grafikleri ve keşif sihirbazı Faz 4 kapsamında eklenecek
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {query.isPending ? (
            <TableSkeleton rows={4} columns={7} />
          ) : query.isError ? (
            <ErrorState
              message="SNMP hedefleri yüklenemedi."
              onRetry={() => void query.refetch()}
            />
          ) : (query.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="SNMP hedefi yok"
              description="Henüz kayıtlı ağ cihazı bulunmuyor."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Adres</TH>
                  <TH>sysName</TH>
                  <TH>Üretici / Model</TH>
                  <TH>Sürüm</TH>
                  <TH>Poll aralığı</TH>
                  <TH>Durum</TH>
                  <TH>Son görülme</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {query.data?.items.map((target) => (
                  <TR key={target.id}>
                    <TD>
                      <span className="font-mono text-sm">{target.ip}</span>
                      <p className="text-muted-foreground text-xs">
                        {target.hostname ?? `:${target.port}`}
                      </p>
                    </TD>
                    <TD className="font-mono text-xs">{target.sysName ?? "—"}</TD>
                    <TD>
                      <div className="flex flex-wrap items-center gap-1">
                        {target.vendor ? <Badge tone="muted">{target.vendor}</Badge> : null}
                        <span className="text-muted-foreground text-xs">
                          {target.model ?? "—"}
                        </span>
                      </div>
                    </TD>
                    <TD>
                      <Badge tone={target.version === "v3" ? "success" : "warning"}>
                        SNMP {target.version}
                      </Badge>
                    </TD>
                    <TD className="text-muted-foreground text-sm">
                      {formatInteger(target.pollIntervalSeconds)} sn
                    </TD>
                    <TD>
                      <DeviceStatusBadge status={target.status} />
                    </TD>
                    <TD className="text-muted-foreground whitespace-nowrap">
                      {target.lastSeen ? formatRelative(target.lastSeen) : "—"}
                    </TD>
                    <TD>
                      <Button size="sm" variant="outline" onClick={() => setSelected(target)}>
                        Detay
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

          {query.data ? (
            <Pagination
              page={query.data.pageMeta.page}
              perPage={query.data.pageMeta.perPage}
              total={query.data.pageMeta.total}
              onPageChange={setPage}
              onPerPageChange={(next) => {
                setPerPage(next);
                setPage(1);
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      {/* Detay yer tutucusu */}
      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.sysName ?? selected.ip}` : "SNMP hedefi"}
        description="Detaylı port trafik grafikleri Faz 4'te eklenecek."
      >
        {selected ? (
          <div>
            <dl className="divide-border divide-y">
              <DetailRow label="IP" value={<span className="font-mono">{selected.ip}</span>} />
              <DetailRow label="Port" value={formatInteger(selected.port)} />
              <DetailRow label="sysName" value={selected.sysName ?? "—"} />
              <DetailRow label="sysDescr" value={selected.sysDescr ?? "—"} />
              <DetailRow label="Üretici" value={selected.vendor ?? "—"} />
              <DetailRow label="Model" value={selected.model ?? "—"} />
              <DetailRow label="SNMP sürümü" value={`SNMP ${selected.version}`} />
              <DetailRow
                label="Poll aralığı"
                value={`${formatInteger(selected.pollIntervalSeconds)} sn`}
              />
              <DetailRow label="Durum" value={<DeviceStatusBadge status={selected.status} />} />
              <DetailRow
                label="Son görülme"
                value={selected.lastSeen ? formatDateTime(selected.lastSeen) : "—"}
              />
              <DetailRow label="Kayıt" value={formatDateTime(selected.createdAt)} />
            </dl>
            <div className="bg-muted/60 text-muted-foreground mt-4 rounded-md border border-border p-3 text-xs">
              Kimlik bilgileri (community / SNMPv3 parolaları) API tarafından asla
              geri döndürülmez. Port bazlı trafik, hata sayaçları ve keşif
              sihirbazı sonraki fazda bu ekrana eklenecek.
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}